import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { HardDrive, Loader2, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Progress + trigger for the one-shot image backfill (see routes/admin.py).
 * Runs on the pod, downloads every remote event image and rehosts it into
 * Emergent Object Storage. Idempotent — re-running skips already-local URLs
 * for free. Polls status every 3s while a job is running.
 */
export default function ImageBackfillCard() {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/images/backfill/status`);
      setStatus(res.data);
    } catch {
      // silent — auth errors will surface on the trigger button
    }
  };

  useEffect(() => {
    fetchStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (status?.running) {
      if (!pollRef.current) {
        pollRef.current = setInterval(fetchStatus, 3000);
      }
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [status?.running]);

  const start = async () => {
    setStarting(true);
    try {
      await axios.post(`${API_URL}/api/admin/images/backfill`);
      toast.success('Migración iniciada');
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar la migración');
    } finally {
      setStarting(false);
    }
  };

  const pct = status?.total ? Math.round((status.processed / status.total) * 100) : 0;
  const done = !!status && status.total > 0 && status.processed >= status.total && !status.running;

  return (
    <Card className="rounded-2xl border-0 shadow-sm mb-6" data-testid="image-backfill-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HardDrive className="w-5 h-5 text-emerald-700" />
          Almacenamiento de imágenes
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-600">
          Descarga las imágenes remotas de todos los eventos y las guarda en el
          almacenamiento propio para que carguen rápido y no se venzan cuando la
          fuente original expira el link (Instagram, Facebook, etc.). Idempotente:
          los eventos ya migrados se saltan.
        </p>

        {status?.running && (
          <div className="space-y-2" data-testid="backfill-running">
            <div className="flex items-center gap-2 text-sm text-emerald-800 font-semibold">
              <Loader2 className="w-4 h-4 animate-spin" />
              Migrando… {status.processed}/{status.total} eventos ({pct}%)
            </div>
            <Progress value={pct} />
            <div className="text-xs text-gray-500 flex flex-wrap gap-3">
              <span>Rehosteados: <b className="text-emerald-700">{status.migrated}</b></span>
              <span>Ya locales: <b>{status.skipped_local}</b></span>
              <span>Fallidos: <b className="text-amber-700">{status.failed}</b></span>
            </div>
          </div>
        )}

        {done && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900 flex items-start gap-2" data-testid="backfill-done">
            <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-700" />
            <div>
              <div className="font-semibold">Migración completada</div>
              <div className="text-xs mt-0.5">
                {status.migrated} rehosteados · {status.skipped_local} ya locales · {status.failed} fallidos
                {status.failed > 0 && (
                  <span className="text-amber-700"> · Los fallidos suelen ser URLs de CDN ya expirados. La foto se muestra con placeholder.</span>
                )}
              </div>
            </div>
          </div>
        )}

        {!status?.running && status?.failed > 0 && !done && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            Última corrida tuvo {status.failed} fallidos (URLs expiradas del origen).
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={start}
            disabled={starting || status?.running}
            className="bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50"
            data-testid="start-image-backfill-btn"
          >
            {starting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            {status?.running ? 'Migrando…' : (done ? 'Volver a correr' : 'Iniciar migración')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStatus}
            data-testid="refresh-image-backfill-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const kick = await axios.post(`${API_URL}/api/admin/images/cleanup-placeholders`);
                if (kick.data?.message?.includes('curso')) {
                  toast.info('Limpieza ya estaba en curso, siguiendo progreso…');
                } else {
                  toast.info('Limpieza iniciada. Puede tardar unos minutos.');
                }
                // Poll until finished.
                const started = Date.now();
                const loop = async () => {
                  const res = await axios.get(`${API_URL}/api/admin/images/cleanup-placeholders/status`);
                  const s = res.data;
                  if (!s.running) {
                    toast.success(
                      `Limpieza terminada: ${s.touched} eventos corregidos ` +
                      `(${s.by_url} por URL, ${s.by_hash} por hash). ` +
                      `${s.hash_errors ? s.hash_errors + ' errores de lectura.' : ''}`
                    );
                    return;
                  }
                  if (Date.now() - started > 15 * 60 * 1000) {
                    toast.warning('Limpieza sigue corriendo en segundo plano.');
                    return;
                  }
                  setTimeout(loop, 3000);
                };
                setTimeout(loop, 1500);
              } catch (e) {
                toast.error('No se pudo iniciar: ' + (e.response?.data?.detail || e.message));
              }
            }}
            className="ml-auto"
            data-testid="cleanup-placeholders-btn"
            title="Vacía la portada de eventos que apuntan al logo genérico de Passline (por URL o por hash del archivo) para que caiga el fallback de PlanCity."
          >
            Limpiar portadas Passline
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
